/**
 * Club page — the POSTS feed model (handoff README §4 "The infinite feed"). Pure.
 *
 * Merges everything a club produces into one list: announcements (POST / PINNED, or OFFER when
 * the post is a linked Pro page's offer cross-posted here), polls, linked events, gatherings,
 * forum threads, live signals and member joins grouped per week (JOINED). Pinned first, then reverse-chronological. The page reveals PAGE items at a time
 * on a sentinel and asks the server for the next announcement page once the visible window
 * nears the end of what is loaded.
 */
import { dayLabel, dayRangeLabel, money, ago, plural, dateLabel, countdown } from '../../utils/surfaceFormat';

export const PAGE = 4;
export const ANNOUNCEMENT_PAGE_SIZE = 20;

export const KIND_TONE = {
  PINNED: 'black', POST: 'ghost', OFFER: 'discoYellow', EVENT: 'discoYellow', GATHERING: 'mint', POLL: 'lilac', PORTAL: 'cyan', FORUM: 'hairline', JOINED: 'lime',
};

const ts = (iso) => { const t = iso ? new Date(iso).getTime() : NaN; return Number.isNaN(t) ? 0 : t; };

/** Monday-anchored ISO week key for grouping joins: `2026-W12` style but computed locally. */
export function weekKey(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const day = (d.getDay() + 6) % 7; // Mon=0
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  return `${monday.getFullYear()}-${monday.getMonth() + 1}-${monday.getDate()}`;
}

/** "Priya Nair and Kit Larsen joined this week" · "A, B and 3 others joined the week of MAR 2". */
export function joinedSentence(names, weekStartIso, now = new Date()) {
  const clean = names.filter(Boolean);
  let who;
  if (clean.length === 0) who = 'Someone';
  else if (clean.length === 1) who = clean[0];
  else if (clean.length === 2) who = `${clean[0]} and ${clean[1]}`;
  else if (clean.length === 3) who = `${clean[0]}, ${clean[1]} and ${clean[2]}`;
  else who = `${clean[0]}, ${clean[1]} and ${clean.length - 2} others`;
  const thisWeek = weekKey(weekStartIso) === weekKey(now);
  return thisWeek ? `${who} joined this week.` : `${who} joined the week of ${dateLabel(weekStartIso)}.`;
}

/** `FRI JUL 17 · 12 of 40 in · $25.00` — only the pieces the ClubEventDto actually carries. */
export function eventLinkNote(e) {
  const parts = [dayLabel(e.dateTime)];
  if (e.acceptedCount != null && e.maxAttendees) parts.push(`${e.acceptedCount} of ${e.maxAttendees} in`);
  else if (e.fundingGoalCents > 0) parts.push(`${money(e.totalPledgedCents || 0)} of ${money(e.fundingGoalCents)} pledged`);
  if (e.ticketPriceCents != null) parts.push(e.ticketPriceCents === 0 ? 'FREE' : money(e.ticketPriceCents));
  else if (e.status) parts.push(String(e.status).replace(/_/g, ' ').toUpperCase());
  return parts.filter(Boolean).join(' · ');
}

/** `JUL 10 – JUL 11 · 3 EVENTS · 14 in · $70.00`. */
export function gatheringLinkNote(g) {
  const parts = [dayRangeLabel(g.startsAt, g.endsAt)];
  if (g.eventCount != null) parts.push(plural(g.eventCount, 'EVENT'));
  if (g.memberCount != null) parts.push(`${g.memberCount} in`);
  // The plain ticket sum when the agenda is ticketed; the entry fee (or FREE) when it isn't.
  if (g.ticketTotalCents > 0) parts.push(money(g.ticketTotalCents));
  else if (g.entryFeeCents != null) parts.push(g.entryFeeCents === 0 ? 'FREE' : money(g.entryFeeCents));
  if (g.status === 'draft') parts.push('DRAFT');
  return parts.filter(Boolean).join(' · ');
}

/** `$20 OFF · CLAIM ON KELOWNA COMEDY` · `ENDED` · `ALL CLAIMED`. */
export function offerLinkNote(o) {
  const parts = [];
  if (o.phase === 'ended') parts.push('ENDED');
  else if (o.phase === 'ran_out') parts.push('ALL CLAIMED');
  else if (o.valueText) parts.push(o.valueText);
  if (o.phase !== 'ended' && o.phase !== 'ran_out' && o.expiresAt) parts.push(`UNTIL ${dateLabel(o.expiresAt)}`);
  parts.push(`CLAIM ON ${(o.pageName || 'THE PAGE').toUpperCase()}`);
  return parts.join(' · ');
}

/** `RIDES · 11 REPLIES · LAST 40M AGO`. */
export function forumLinkNote(t, now = new Date()) {
  const parts = [];
  if (t.categoryName) parts.push(t.categoryName.toUpperCase());
  parts.push(plural(t.replyCount || 0, 'REPLY', 'REPLIES'));
  if (t.lastActivityAt) parts.push(`LAST ${ago(t.lastActivityAt, now)}`);
  return parts.join(' · ');
}

/** `EXPIRES IN 2H 14M` · `EXPIRED` · `LIVE` when the signal carries no end. */
export function signalExpiryNote(s, now = new Date()) {
  if (!s?.expiresAtUtc) return 'LIVE';
  const c = countdown(s.expiresAtUtc, { now });
  return c.expired ? 'EXPIRED' : `EXPIRES IN ${c.text}`;
}

/**
 * Build the merged feed. Every input is optional; the club name is the "who" on event,
 * gathering and joined cards.
 */
export function buildFeed({
  announcements = [], polls = [], events = [], gatherings = [], threads = [], signals = [], members = [],
  clubName = '', now = new Date(),
} = {}) {
  const items = [];

  announcements.forEach((a) => {
    const offer = a.offer || null;
    const item = {
      key: `ann-${a.id}`, kind: a.isPinned ? 'PINNED' : offer ? 'OFFER' : 'POST', pinned: !!a.isPinned, sortAt: ts(a.createdAt),
      who: a.authorDisplayName || 'MEMBER', avatarUrl: a.authorAvatarUrl, authorUserId: a.authorUserId,
      when: a.isPinned ? 'PINNED BY AN ADMIN' : ago(a.createdAt, now), body: a.content, announcement: a,
      edited: !!(a.updatedAt && a.createdAt && ts(a.updatedAt) - ts(a.createdAt) > 1000),
    };
    // A cross-posted Pro offer: the page is the "who", and the card carries the offer to claim.
    if (offer) {
      item.who = offer.pageName || item.who;
      item.avatarUrl = offer.pageLogoUrl || item.avatarUrl;
      item.offer = offer;
      item.link = { label: offer.title, note: offerLinkNote(offer), to: offer.path || `/pro/${offer.pageSlug}/offers`, imageUrl: offer.photoUrl || offer.pageLogoUrl, key: offer.id };
    }
    items.push(item);
  });

  polls.forEach((p) => {
    items.push({
      key: `poll-${p.id}`, kind: 'POLL', pinned: false, sortAt: ts(p.createdAt),
      who: p.authorDisplayName || 'ADMIN', avatarUrl: p.authorAvatarUrl, when: ago(p.createdAt, now),
      title: p.question, poll: p,
    });
  });

  events.forEach((e) => {
    if (e.isHiddenInClub) return;
    items.push({
      key: `event-${e.id}`, kind: 'EVENT', pinned: false, sortAt: ts(e.linkedAt || e.createdAt || e.dateTime),
      who: clubName || 'THE CLUB', avatarUrl: null, when: e.linkedAt || e.createdAt ? ago(e.linkedAt || e.createdAt, now) : dayLabel(e.dateTime),
      title: e.title, body: 'New event on the club calendar.',
      link: { label: e.title, note: eventLinkNote(e), to: `/events/${e.inviteCode || e.id}`, imageUrl: e.posterUrl, key: e.id },
      event: e,
    });
  });

  gatherings.forEach((g) => {
    items.push({
      key: `gath-${g.id}`, kind: 'GATHERING', pinned: false, sortAt: ts(g.createdAt || g.startsAt),
      who: clubName || 'THE CLUB', avatarUrl: null, when: g.createdAt ? ago(g.createdAt, now) : dayRangeLabel(g.startsAt, g.endsAt),
      title: g.name, body: g.description || `${plural(g.eventCount || 0, 'event')}, one weekend.`,
      link: { label: g.name, note: gatheringLinkNote(g), to: `/clubs/${g.clubId}/gatherings/${g.id}`, imageUrl: g.posterUrl, key: g.id },
      gathering: g,
    });
  });

  threads.forEach((t) => {
    items.push({
      key: `thread-${t.id}`, kind: 'FORUM', pinned: false, sortAt: ts(t.createdAt),
      who: t.authorName || 'MEMBER', avatarUrl: t.authorAvatarUrl, when: ago(t.createdAt, now),
      title: t.title, body: t.body,
      link: { label: t.title, note: forumLinkNote(t, now), to: `/clubs/${t.clubId}/forum/${t.id}`, key: t.id },
      thread: t,
    });
  });

  signals.forEach((s) => {
    items.push({
      key: `signal-${s.eventId}`, kind: 'PORTAL', pinned: false, sortAt: ts(s.createdAt || s.dateTimeUtc),
      who: s.hostDisplayName || 'MEMBER', avatarUrl: s.hostAvatarUrl, when: s.createdAt ? ago(s.createdAt, now) : 'LIVE',
      body: s.signalText, signal: s,
      link: { label: 'OPEN THE PORTAL', note: signalExpiryNote(s, now), to: `/portals/${s.eventId}`, key: s.eventId },
    });
  });

  const weeks = new Map();
  members.forEach((m) => {
    if (!m.joinedAt) return;
    const k = weekKey(m.joinedAt);
    if (!weeks.has(k)) weeks.set(k, { names: [], members: [], latest: 0, first: m.joinedAt });
    const w = weeks.get(k);
    w.names.push(m.displayName);
    w.members.push(m);
    w.latest = Math.max(w.latest, ts(m.joinedAt));
    if (ts(m.joinedAt) < ts(w.first)) w.first = m.joinedAt;
  });
  weeks.forEach((w, k) => {
    items.push({
      key: `joined-${k}`, kind: 'JOINED', pinned: false, sortAt: w.latest,
      who: clubName || 'THE CLUB', avatarUrl: null, when: ago(new Date(w.latest).toISOString(), now),
      body: joinedSentence(w.names, w.first, now), joined: w.members,
    });
  });

  items.sort((a, b) => (Number(b.pinned) - Number(a.pinned)) || (b.sortAt - a.sortAt));
  return items;
}

/** `SCROLL FOR MORE` → `LOADING MORE…` → `THAT'S EVERYTHING.` */
export function feedStatus({ revealed, total, serverHasMore, loading }) {
  if (loading) return 'LOADING MORE…';
  if (revealed >= total && !serverHasMore) return "THAT'S EVERYTHING.";
  return 'SCROLL FOR MORE';
}

/** Recompute a poll after one vote — the option gains a vote and the viewer's index is set. */
export function applyVote(poll, optionIndex) {
  if (!poll || poll.userVoteIndex != null) return poll;
  return {
    ...poll,
    userVoteIndex: optionIndex,
    totalVotes: (poll.totalVotes || 0) + 1,
    options: (poll.options || []).map((o) => (o.index === optionIndex ? { ...o, voteCount: (o.voteCount || 0) + 1 } : o)),
  };
}

export function pollPercent(poll, option) {
  const total = poll?.totalVotes || 0;
  if (!total) return 0;
  return Math.round(((option.voteCount || 0) / total) * 100);
}

/** Announcements per week, from what is loaded, when the DTO has no `postsLast7Days`. */
export function postsLastWeek(announcements = [], now = new Date()) {
  const cutoff = now.getTime() - 7 * 86400000;
  return announcements.filter((a) => ts(a.createdAt) >= cutoff).length;
}

export default buildFeed;
